import { useEffect, useRef } from "react";
import { ImageContent } from "src/views/assistant/types";
import { type SendMessageOptions } from "src/reusable/Chat/ChatStreaming/stores/CoreStreamingSlice";

const useStartChatOnMount = ({
  initialUserMessage,
  initialImages,
  initialSendId,
  clearInitialMessage,
  emptySubmission,
  sendUserMessage,
}: {
  initialUserMessage?: string;
  /** VLM images to include with the initial message (from splash screen) */
  initialImages?: ImageContent[];
  /** send_id already captured with ai_message_sent at splash submit, so the
   *  completion event pairs with it (#29704) */
  initialSendId?: string;
  /** Callback to clear the initial message after sending (prevents duplicate on refresh) */
  clearInitialMessage?: () => void;
  emptySubmission: () => Promise<boolean>;
  sendUserMessage: (
    message: string,
    images?: ImageContent[],
    opts?: SendMessageOptions,
  ) => Promise<boolean>;
}) => {
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple calls
    if (hasStartedRef.current) {
      return;
    }

    const sendMessage = async () => {
      if (initialUserMessage !== undefined) {
        hasStartedRef.current = true;

        try {
          // Add a small delay to ensure the component is fully mounted
          // and all context/stores are properly initialized
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Check if this is truly an empty submission (no text AND no images)
          // vs an image-only message (no text but images present)
          const hasImages = initialImages && initialImages.length > 0;

          if (!initialUserMessage && !hasImages) {
            await emptySubmission();
          } else {
            await sendUserMessage(initialUserMessage || "", initialImages, {
              sendId: initialSendId,
              target: "chat",
            });
          }

          // Clear the initial message after sending to prevent duplicate on page refresh
          // This is critical: sessionStorage persists the message, so we must clear it
          if (clearInitialMessage) {
            clearInitialMessage();
          }
        } catch {
          // Allow retry on failure
          hasStartedRef.current = false;
        }
      }
    };

    sendMessage();
  }, [
    initialUserMessage,
    initialImages,
    initialSendId,
    clearInitialMessage,
    emptySubmission,
    sendUserMessage,
  ]);
};

export default useStartChatOnMount;
